import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { breakpoint } from '@zocdoc/components/src/grid';
import { FontFamily } from '@zocdoc/components/src/typography';
import { TOOLTIP, Tooltip } from '@zocdoc/components/src/Tooltip';
import { Button } from '@zocdoc/components/src/button';
import CloseIcon from '@zocdoc/components/src/icons/Close';
import { blobBackgroundColor, LOCALIZED_STRINGS, tooltipBackgroundColor } from './constants';
import { maybeSetProviderAvailabilityTooltipCookie } from './utils';
import TooltipBlob from './assets/TooltipBlob.svg';
import TooltipBlobMobile from './assets/TooltipBlobMobile.svg';
import MagnifyingGlassImage from './assets/MagnifyingGlass.svg';

class ProviderAvailabilityTooltipView extends Component {
    static propTypes = {
        shouldDisplayProviderAvailabilityTooltip: PropTypes.bool.isRequired,
        providerAvailabilityData: PropTypes.object.isRequired,
        specialty: PropTypes.object.isRequired,
        trackProviderAvailabilityClickedClose: PropTypes.func.isRequired,
        trackProviderAvailabilityClickedSearch: PropTypes.func.isRequired,
        trackProviderAvailabilityTooltipRendered: PropTypes.func.isRequired,
        user: PropTypes.shape({
            displayName: PropTypes.string,
            loginState: PropTypes.number,
        }).isRequired,
    };

    state = {
        userClosedTooltip: false,
    };

    componentDidMount() {
        if (this.shouldRenderTooltip()) {
            this.handleTooltipRendered();
        }
        maybeSetProviderAvailabilityTooltipCookie();
    }

    render() {
        if (this.shouldRenderTooltip()) {
            const {
                specialty: {
                    id,
                    name,
                },
                user: {
                    displayName,
                },
            } = this.props;

            const tooltipBody = this.renderTooltip(id, name, displayName);

            // N.B., CSS is used to selectively load mobile vs desktop view
            return (
                <ProviderAvailabilityTooltip>
                    {this.renderTooltipMobile(tooltipBody)}
                    {this.renderTooltipDesktop(tooltipBody)}
                </ProviderAvailabilityTooltip>
            );
        }
        return null;
    }

    renderTooltipDesktop(tooltipBody) {
        return (
            <ProviderAvailabilityTooltipWrapper
                data-test="provider-availability-tooltip"
                showTooltip
                tooltipColor={blobBackgroundColor}
                tooltipPosition={TOOLTIP.LEFT}
            >
                {tooltipBody}
            </ProviderAvailabilityTooltipWrapper>
        );
    }

    renderTooltipMobile(tooltipBody) {
        if (this.state.clickedClose) {
            return null;
        }

        return (
            <ProviderAvailabilityTooltipWrapperMobile
                data-test="provider-availability-tooltip-mobile"
            >
                {tooltipBody}
            </ProviderAvailabilityTooltipWrapperMobile>
        );
    }

    renderTooltip(specialtyId, specialtyName, firstName) {
        return (
            <React.Fragment>
                <AvailabilityTooltip>
                    <PersonalizedMessageRow>
                        <ProviderAvailabilityText>
                            <ProviderAvailabilityTooltipHeader
                                data-test="provider-availability-header-text"
                            >
                                <FormattedMessage
                                    {...LOCALIZED_STRINGS.ProviderAvailabilityHeader}
                                    values={{ firstName }}
                                />
                            </ProviderAvailabilityTooltipHeader>
                            <ProviderAvailabilityTooltipMessage
                                data-test="provider-availability-message-text"
                            >
                                <FormattedMessage
                                    {...LOCALIZED_STRINGS.ProviderAvailabilityMessage}
                                />
                            </ProviderAvailabilityTooltipMessage>
                        </ProviderAvailabilityText>
                        <div>
                            <MagnifyingGlass
                                data-test="magnifying-glass-image"
                            />
                        </div>
                    </PersonalizedMessageRow>
                    <ProviderAvailabilityTooltipButton
                        beforeClick={this.handleClickSearchButton}
                        data-test="provider-availability-search-button"
                        href={this.buildSearchUrl(specialtyId)}
                    >
                        <FormattedMessage
                            {...LOCALIZED_STRINGS.ProviderAvailabilityButton}
                            values={{ specialtyName }}
                        />
                    </ProviderAvailabilityTooltipButton>
                </AvailabilityTooltip>
                <CloseWrapper
                    data-test="close-button"
                    onClick={this.handleClickClose}
                >
                    <CloseIcon />
                </CloseWrapper>
            </React.Fragment>
        );
    }

    handleClickSearchButton = () => {
        const {
            specialty: {
                id,
            },
            trackProviderAvailabilityClickedSearch,
        } = this.props;

        trackProviderAvailabilityClickedSearch(id);
    };

    handleTooltipRendered = () => {
        const {
            providerAvailabilityData,
            trackProviderAvailabilityTooltipRendered,
        } = this.props;

        trackProviderAvailabilityTooltipRendered(providerAvailabilityData);
    };

    handleClickClose = () => {
        this.setState({
            userClosedTooltip: true,
        });

        this.props.trackProviderAvailabilityClickedClose();
    };

    buildSearchUrl(specialtyId) {
        return `/search?dr_specialty=${specialtyId}`;
    }

    shouldRenderTooltip = () => {
        const {
            shouldDisplayProviderAvailabilityTooltip,
        } = this.props;

        const { userClosedTooltip } = this.state;

        return shouldDisplayProviderAvailabilityTooltip && !userClosedTooltip;
    }
}

/* region Styles */

const ProviderAvailabilityTooltipWrapper = styled(Tooltip)`
    background-color: ${tooltipBackgroundColor}; 
    background-image: url(${TooltipBlob});
    background-position: right;
    background-repeat: no-repeat;
    background-size: auto 100%;
    display: flex;
    flex-direction: row;
    padding: 18px 20px 28px 20px;
    width: 545px;

    ${breakpoint('small', { down: true })} {
        display: none;
    }
`;

const ProviderAvailabilityTooltipWrapperMobile = styled.div`
    background-color: ${tooltipBackgroundColor};
    background-image: url(${TooltipBlobMobile});
    background-position: bottom right;
    background-repeat: no-repeat;
    background-size: auto 55%;
    display: block;
    position: fixed;
    width: 100%;
    height: 142px;
    bottom: 0;
    padding: 16px 16px 16px 14px;
    z-index: 1;

    ${breakpoint.medium} {
        display: none;
    }
`;

const AvailabilityTooltip = styled.div`
    display: flex;
    flex-direction: column;
`;

const PersonalizedMessageRow = styled.div`
    display: flex;
    flex-direction: row;
`;

const ProviderAvailabilityText = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: 8px;
    
    ${breakpoint.small} {
        padding-right: 30px;
    }
`;

const ProviderAvailabilityTooltipHeader = styled.div`
    font: 15px/1.33 ${FontFamily.SharpSansSemibold};
    margin-bottom: 3px;
    ${breakpoint.medium} {
        font: 22px/1.18 ${FontFamily.SharpSansSemibold};
        margin-bottom: 0px;
    }
`;

const ProviderAvailabilityTooltipMessage = styled.div`
    font: 13px/1.5 ${FontFamily.SharpSansMedium};    
    ${breakpoint.medium} {
        font: 16px/1.5 ${FontFamily.SharpSansMedium};
    }
`;

const MagnifyingGlass = styled.div`
    background: url(${MagnifyingGlassImage}) center no-repeat;
    background-size: auto;
    height: 107px;
    width: 108px;
    ${breakpoint('small', { down: true })} {
        display: none;
    }
`;

const ProviderAvailabilityTooltipButton = styled(Button)`
    font: 13px ${FontFamily.SharpSansSemibold};
    margin-top: 7px;
    ${breakpoint.medium} {
        font: 18px ${FontFamily.SharpSansSemibold};
        margin-top: 7px;
    }
`;

const CloseWrapper = styled.span`
    cursor: pointer;
    ${breakpoint.medium} {
        height: 0;
        transform: translateX(-20px);
        width: 0;
    }
    ${breakpoint('small', { down: true })} {
        top: 14px;
        right: 14px;
        position: absolute;
    }
`;

const ProviderAvailabilityTooltip = styled.div`
    margin: auto;
`;

/* endregion Styles */

export {
    ProviderAvailabilityTooltipView,
};
